import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { driverApi, orgApi } from "@/lib/entities";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { DemoChip } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { Pager } from "@/pages/customers/CustomersList";
import { DriverFormDialog } from "@/pages/drivers/DriverFormDialog";
import { ExpirationBadge, ExpirationRow } from "@/components/ExpirationBadge";
import { expirationStatus } from "@/lib/expirationStatus";
import { ActionMenu, ArchiveConfirmModal, useArchive } from "@/components/ActionMenu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DriverSearchPanel from "@/components/DriverSearchPanel";
import TourSelectorModal from "@/components/TourSelectorModal";

// PRG · Wave 4B-6 canonical resolver. `can("drivers.edit")` auto-grants
// Standard Admin the same write capability that Super Admin and
// ops_dispatch have on the driver roster.
const READ_ROLES = ["super_admin", "admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];

export default function DriversList() {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [activeOnly, setActiveOnly] = useState(true);
  const [expiringOnly, setExpiringOnly] = useState(false);
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") === "search" ? "search" : "list";
  const setView = (v) => {
    if (v === "search") setSearchParams({ view: "search" });
    else setSearchParams({});
  };
  const canWrite = can("drivers.edit");
  // Correction Batch STEP 1 — Advanced Search co-driver visibility.
  // Both actions gate on the SAME server-side permission
  // (`fleet.assign`) that already governs POST /api/tours/{id}/drivers.
  // No new permission is introduced.
  const canAssign = can("fleet.assign");
  const [assignTarget, setAssignTarget] = useState(null); // { driver, mode }

  const load = async () => {
    try {
      const d = await driverApi.list({
        q: q || undefined,
        active: activeOnly ? true : undefined,
        page,
        page_size: 25,
      });
      let rs = d.results;
      if (expiringOnly) {
        // RC10i — use shared helper so filter + display stay consistent.
        const isExpiringOrOverdue = (val, accessState) => {
          const s = expirationStatus(val, { accessState });
          return s.status === "warning" || s.status === "expired";
        };
        rs = rs.filter(
          (r) =>
            isExpiringOrOverdue(r.cdl_expiration) ||
            isExpiringOrOverdue(r.medical_card_expiration) ||
            isExpiringOrOverdue(r.passport_expiration, r.passport_access)
        );
      }
      setRows(rs || []);
      setTotal(d.total);
    } catch {
      toast.error("Failed to load drivers");
    }
  };

  const archive = useArchive({
    entityLabel: "Driver",
    apiArchive: driverApi.archive,
    onSuccess: async (row) => {
      toast.success(`Archived ${row.display_name}`);
      await load();
    },
  });

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, [q, page, activeOnly, expiringOnly]);

  useEffect(() => {
    // Populate employer dropdown in DriverFormDialog. Non-blocking.
    orgApi.list({ page_size: 200 }).then((d) => setOrgs(d.results || [])).catch(() => {});
  }, []);

  return (
    <RoleGate allow={READ_ROLES}>
      <PageShell>
        {/* RC4 · shared SectionHeader adoption landmark (sr-only until full visual migration lands progressively). */}
        <SectionHeader testid="page-section-landmark" className="sr-only">Drivers</SectionHeader>
        <PageHeader
          testid="page-drivers"
          title="Drivers"
          subtitle={view === "search"
            ? "Eligibility-aware search — filter by Client, dates, license, passport, and availability"
            : `${total.toLocaleString()} total`}
          right={
            canWrite && (
              <button
                data-testid="new-driver-btn"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm">
                <Plus size={14} /> New Driver
              </button>
            )
          }
        />

        <Tabs value={view} onValueChange={setView} className="mt-2">
          <TabsList
            data-testid="drivers-view-tabs"
            className="bg-white border border-coach-silver rounded-sm w-full justify-start overflow-x-auto flex-nowrap">
            <TabsTrigger
              value="list"
              data-testid="drivers-tab-list"
              className="shrink-0 whitespace-nowrap data-[state=active]:bg-coach-blue data-[state=active]:text-white">
              <span className="sm:hidden">Roster</span>
              <span className="hidden sm:inline">Driver Roster</span>
            </TabsTrigger>
            <TabsTrigger
              value="search"
              data-testid="drivers-tab-search"
              className="shrink-0 whitespace-nowrap data-[state=active]:bg-coach-blue data-[state=active]:text-white">
              <span className="sm:hidden">Advanced Search</span>
              <span className="hidden sm:inline">Advanced Search (Eligibility)</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="mt-3">
            <DriverSearchPanel
              onAssignDriver={canAssign ? (row) => setAssignTarget({ driver: row.driver, mode: "assign" }) : null}
              onAddCoDriver={canAssign ? (row) => setAssignTarget({ driver: row.driver, mode: "co_driver" }) : null}
            />
          </TabsContent>

          <TabsContent value="list" className="mt-3">
            <div className="bg-white border border-coach-silver rounded-md">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-coach-silver">
            <input
              data-testid="drivers-search"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              placeholder="Search by name, driver #, CDL…"
              className="flex-1 max-w-md border border-coach-silver rounded-sm px-3 py-1.5 text-[13px]"
            />
            <label className="text-[12px] text-steel flex items-center gap-1.5">
              <input
                type="checkbox"
                data-testid="drivers-active-only"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />{" "}
              Active only
            </label>
            <label className="text-[12px] text-steel flex items-center gap-1.5">
              <input
                type="checkbox"
                data-testid="drivers-expiring"
                checked={expiringOnly}
                onChange={(e) => setExpiringOnly(e.target.checked)}
              />{" "}
              Expiring soon
            </label>
          </div>

          <table className="hidden md:table w-full text-[13px]" data-testid="drivers-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                {/* RC4 · ITEM 8 — Driver # column hidden from UI.
                    Identifier is still persisted server-side and
                    accepted by search/filter APIs for compatibility. */}
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Phone</th>
                <th className="text-left px-4 py-2 w-56">CDL Exp.</th>
                <th className="text-left px-4 py-2 w-56">Medical Exp.</th>
                <th className="text-left px-4 py-2 w-56">Passport Exp.</th>
                <th className="text-left px-4 py-2 w-20">Active</th>
                <th className="text-right px-4 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows && rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`driver-row-${r.driver_number}`}
                  className="border-t border-coach-silver/60 hover:bg-road-white"
                >
                  <td className="px-4 py-2">
                    <Link to={`/drivers/${r.id}`} className="text-coach-blue hover:underline">
                      {r.display_name}
                    </Link>{" "}
                    {r.is_demo && <DemoChip />}
                  </td>
                  <td className="px-4 py-2 text-steel">{r.phone_primary || "—"}</td>
                  <td className="px-4 py-2">
                    <ExpirationBadge
                      value={r.cdl_expiration}
                      testid={`driver-${r.driver_number}-cdl-exp`}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <ExpirationBadge
                      value={r.medical_card_expiration}
                      testid={`driver-${r.driver_number}-med-exp`}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <ExpirationBadge
                      value={r.passport_expiration}
                      accessState={r.passport_access === "restricted" ? "restricted" : undefined}
                      testid={`driver-${r.driver_number}-passport-exp`}
                    />
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={r.active ? "text-touring-lime font-medium" : "text-critical-red font-medium"}
                    >
                      {r.active ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right">
                    <ActionMenu
                      testid={`driver-actions-${r.driver_number}`}
                      onView={() => nav(`/drivers/${r.id}`)}
                      onEdit={canWrite ? () => nav(`/drivers/${r.id}?edit=1`) : undefined}
                      onArchive={canWrite && !r.is_archived ? () => archive.request(r) : undefined}
                    />
                  </td>
                </tr>
              ))}
              {rows === null && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-steel">
                    Loading drivers…
                  </td>
                </tr>
              )}
              {rows && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-steel">
                    No drivers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Mobile cards — RC10i: three expiration rows per driver,
              preserving row-click → /drivers/<id>. */}
          <div className="md:hidden space-y-3 p-3" data-testid="drivers-mobile-list">
            {rows === null && (
              <div className="text-center text-steel text-[13px] py-8">Loading drivers…</div>
            )}
            {rows && rows.length === 0 && (
              <div className="text-center text-steel text-[13px] py-8">No drivers found.</div>
            )}
            {(rows || []).map((r) => (
              <Link
                key={r.id}
                to={`/drivers/${r.id}`}
                data-testid={`driver-card-${r.driver_number}`}
                className="block hover:no-underline"
              >
                <div className="bg-white border border-coach-silver rounded-md shadow-sm p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[15px] font-semibold text-carbon leading-tight break-words">
                          {r.display_name}
                        </div>
                        {r.is_demo && <DemoChip />}
                      </div>
                      <div className="text-[13px] text-steel mt-0.5">{r.phone_primary || "—"}</div>
                    </div>
                    <span
                      className={`text-[11px] font-semibold uppercase tracking-wide shrink-0 ${
                        r.active ? "text-touring-lime" : "text-critical-red"
                      }`}
                    >
                      {r.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="mt-2 border-t border-coach-silver/60 pt-1">
                    <ExpirationRow
                      label="CDL"
                      value={r.cdl_expiration}
                      testid={`driver-${r.driver_number}-cdl-exp-m`}
                    />
                    <ExpirationRow
                      label="Medical"
                      value={r.medical_card_expiration}
                      testid={`driver-${r.driver_number}-med-exp-m`}
                    />
                    <ExpirationRow
                      label="Passport"
                      value={r.passport_expiration}
                      accessState={r.passport_access === "restricted" ? "restricted" : undefined}
                      testid={`driver-${r.driver_number}-passport-exp-m`}
                    />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <Pager page={page} setPage={setPage} total={total} pageSize={25} />
        </div>
          </TabsContent>
        </Tabs>

        {open && (
          <DriverFormDialog
            open={open}
            organizations={orgs}
            onClose={() => setOpen(false)}
            onSaved={() => {
              setOpen(false);
              load();
            }}
          />
        )}
        <ArchiveConfirmModal {...archive.modalProps} />
        <TourSelectorModal
          open={!!assignTarget}
          driver={assignTarget?.driver}
          mode={assignTarget?.mode}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { /* refresh not strictly needed on this page — row state unchanged */ }}
        />
      </PageShell>
    </RoleGate>
  );
}
